import React, { useEffect, useMemo } from 'react';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Select } from './Select';
import { useYardBlocksQuery } from '../../hooks';
import { i18n } from '../../i18n';
import { Input, LoadingIcon } from '../input';
import { INPUT_ERRORS, REGEX } from '../../const';
import { getCache } from '../../services';
import { showToRoles } from '../../utils';
import { BlockWithMinMaxOptional, User, YardGetBlocksQuery } from '../../types';
import { Button } from '../button';

let hasPlace = false;
export const getHasYardPlaceEmployee = () => hasPlace;
export const getIsValidPlace = (key: string, block = '', rows = '', isRow = true, user: User) => {
  const keys = ['yard-short', key];
  const queryKeys = showToRoles(['YARD_MANAGER', 'YARD_DISPATCHER'], user)
    ? user.yardEmployee.yardId
    : JSON.stringify({ ...keys });
  const data = getCache<YardGetBlocksQuery['data']>(['yard', 'yard-blocks', queryKeys]) ?? [];

  const rangeData = data.find((item) => item.block === block);
  const rangeDataArray = isRow ? rangeData?.rows : rangeData?.slots;
  return REGEX.BLOCK_ROWS.test(rows, rangeDataArray);
};
export const getIsValidAssignedTo = (key: string, assignedTo: any, user: User) => {
  return (
    !!assignedTo.length &&
    !assignedTo.some((item: any) => {
      return (
        !item.userId ||
        !item.blocks.length ||
        item.blocks.some((b: any) => {
          if (!b.block) return true;
          if (b.rowMin && !b.rowMax) return true;
          if (b.slotMin && !b.slotMax) return true;
          if (
            b.rowMin &&
            b.rowMax &&
            !getIsValidPlace(key, b.block, `${b.rowMin}-${b.rowMax}`, true, user)
          )
            return true;
          if (
            b.slotMin &&
            b.slotMax &&
            !getIsValidPlace(key, b.block, `${b.slotMin}-${b.slotMax}`, false, user)
          )
            return true;
        })
      );
    })
  );
};

interface YardPlaceSelectsProps {
  yardId?: string;
  clientYardId?: string;
  containerId?: string;
  block: string;
  rows?: string;
  slots?: string;
  setBlock: (block: any) => void;
  setRows?: (rows: string) => void;
  setSlots?: (slots: string) => void;
  isSubmitClicked?: boolean;
  withDefaultValues?: boolean;
  onNotFound?: () => void;
}

export const YardPlaceEmployeeSelects: React.FC<YardPlaceSelectsProps> = ({
  yardId,
  clientYardId,
  containerId,
  block,
  rows,
  slots,
  setBlock,
  setRows,
  setSlots,
  isSubmitClicked,
  withDefaultValues,
  onNotFound,
}: YardPlaceSelectsProps) => {
  const { data = [], isFetching } = useYardBlocksQuery(
    ['yard-short', yardId || clientYardId || containerId],
    {
      yardId,
      containerId,
      clientYardId,
    },
  );
  const dataRows = useMemo(() => data.find((item) => item.block === block)?.rows, [data, block]);
  const dataSlots = useMemo(() => data.find((item) => item.block === block)?.slots, [data, block]);
  const blockKey = useMemo(() => Math.random(), [block]);

  useEffect(() => {
    if (withDefaultValues) return;

    setBlock('');
    if (setRows) setRows('');
    if (setSlots) setSlots('');
  }, [yardId, clientYardId, containerId]);

  useEffect(() => {
    if (isFetching) {
      hasPlace = true;
      return;
    }

    hasPlace = !!data.length;
    if (onNotFound && !hasPlace) onNotFound();
  }, [!!data.length, isFetching]);

  const validators = {
    rows: rows
      ? !REGEX.BLOCK_ROWS.test(rows, data.find((item) => item.block === block)?.rows)
        ? INPUT_ERRORS.NOT_VALID.BLOCK_ROWS
        : ''
      : '',
    slots: slots
      ? !REGEX.BLOCK_ROWS.test(slots, data.find((item) => item.block === block)?.slots)
        ? INPUT_ERRORS.NOT_VALID.BLOCK_SLOTS
        : ''
      : '',
  };

  if (!isFetching && !data.length) return <></>;
  return (
    <React.Fragment key={blockKey}>
      <Select
        label={i18n.t('label.block') + '*'}
        options={isFetching ? [] : data.map((item) => ({ label: item.block, value: item.block }))}
        value={isFetching ? '' : block}
        onChange={(e) => {
          setBlock(e.target.value);
        }}
        IconComponent={isFetching ? LoadingIcon : undefined}
        error={isSubmitClicked && !block}
        helperText={isSubmitClicked && !block && INPUT_ERRORS.REQUIRED.BLOCK}
      />
      {setRows &&
        (dataRows ? (
          <Input
            id={i18n.t('label.rows') + Math.random()}
            label={i18n.t('label.rows')}
            placeholder={`${dataRows[0]}-${dataRows[dataRows.length - 1]}`}
            defaultValue={rows}
            onChange={(e: any) => {
              setRows(e.target.value);
            }}
            type={'text'}
            data-size={'medium'}
            error={isSubmitClicked && !!validators.rows}
            helperText={isSubmitClicked && validators.rows}
          />
        ) : (
          <span />
        ))}
      {setSlots &&
        (dataSlots ? (
          <Input
            id={i18n.t('label.slots') + Math.random()}
            label={i18n.t('label.slots')}
            placeholder={`${dataSlots[0]}-${dataSlots[dataSlots.length - 1]}`}
            defaultValue={slots}
            onChange={(e: any) => {
              setSlots(e.target.value);
            }}
            type={'text'}
            data-size={'medium'}
            error={isSubmitClicked && !!validators.slots}
            helperText={isSubmitClicked && validators.slots}
            disabled={!REGEX.BLOCK_ROWS.test(rows || '')}
          />
        ) : (
          <span />
        ))}
    </React.Fragment>
  );
};

interface YardPlaceEmployeeBlocksMultiSelectProps {
  yardId?: string;
  clientYardId?: string;
  containerId?: string;
  blocks: BlockWithMinMaxOptional[];
  setBlocks: (blocks: BlockWithMinMaxOptional[]) => void;
  isSubmitClicked?: boolean;
  withDefaultValues?: boolean;
  onNotFound?: () => void;
}

export const YardPlaceEmployeeBlocksMultiSelect: React.FC<YardPlaceEmployeeBlocksMultiSelectProps> = ({
  yardId,
  clientYardId,
  containerId,
  blocks,
  setBlocks,
  isSubmitClicked,
  withDefaultValues,
  onNotFound,
}) => {
  const { data = [], isFetching } = useYardBlocksQuery(
    ['yard-short', yardId || clientYardId || containerId],
    {
      yardId,
      containerId,
      clientYardId,
    },
  );

  const selectedBlocks = blocks.map((item) => item.block).filter(Boolean);

  useEffect(() => {
    if (withDefaultValues) return;

    setBlocks([]);
  }, [yardId, clientYardId, containerId]);

  useEffect(() => {
    if (isFetching) {
      hasPlace = true;
      return;
    }

    hasPlace = !!data.length;
    if (onNotFound && !hasPlace) onNotFound();
  }, [!!data.length, isFetching]);

  if (!isFetching && !data.length) return <></>;

  return (
    <Select
      label={i18n.t('label.block') + '*'}
      options={isFetching ? [] : data.map((item) => ({ label: item.block, value: item.block }))}
      value={isFetching ? '' : [selectedBlocks.join(',')]}
      onSelect={(value: any) => {
        if (selectedBlocks.includes(value)) {
          setBlocks(selectedBlocks.filter((item) => item !== value).map((block) => ({ block })));
          return;
        }

        setBlocks([...selectedBlocks, value].map((block) => ({ block })));
      }}
      IconComponent={isFetching ? LoadingIcon : undefined}
      error={isSubmitClicked && !selectedBlocks.length}
      helperText={isSubmitClicked && !selectedBlocks.length && INPUT_ERRORS.REQUIRED.BLOCK}
      selectedAreasEqaul={{ key: 'value', keys: selectedBlocks }}
      multiple
    />
  );
};

interface YardPlaceSimpleSelectsProps {
  withCreateButtons?: boolean;
  onlyBlock?: boolean;
  yardId?: string;
  clientYardId?: string;
  containerId?: string;
  block: BlockWithMinMaxOptional;
  setAssignedTo: (assignedTo: any) => void;
  isSubmitClicked?: boolean;
  withDefaultValues?: boolean;
  handleDeleteClick?: () => void;
  handlePlusClick?: () => void;
  onNotFound?: () => void;
}

export const YardPlaceEmployeeSimpleSelects: React.FC<YardPlaceSimpleSelectsProps> = ({
  withCreateButtons,
  onlyBlock,
  yardId,
  clientYardId,
  containerId,
  block,
  setAssignedTo,
  isSubmitClicked,
  withDefaultValues,
  handleDeleteClick,
  handlePlusClick,
  onNotFound,
}) => {
  return (
    <>
      <YardPlaceEmployeeSelects
        yardId={yardId}
        clientYardId={clientYardId}
        containerId={containerId}
        block={block.block}
        rows={block.rowMin ? `${block.rowMin}-${block.rowMax || ''}` : ''}
        slots={block.slotMin ? `${block.slotMin}-${block.slotMax || ''}` : ''}
        isSubmitClicked={isSubmitClicked}
        withDefaultValues={withDefaultValues}
        setBlock={(value) =>
          setAssignedTo({
            block: value,
            ...(onlyBlock
              ? {}
              : {
                  rowMin: null,
                  rowMax: null,
                  slotMin: null,
                  slotMax: null,
                }),
          })
        }
        setRows={
          onlyBlock
            ? undefined
            : (value) =>
                setAssignedTo({
                  ...block,
                  rowMin: value.split('-')[0],
                  rowMax: value.split('-')[1],
                })
        }
        setSlots={
          onlyBlock
            ? undefined
            : (value) =>
                setAssignedTo({
                  ...block,
                  slotMin: value.split('-')[0],
                  slotMax: value.split('-')[1],
                })
        }
        onNotFound={onNotFound}
      />
      {!withCreateButtons && (
        <Button
          className={'row-button-square'}
          size={'medium'}
          data-color={'destructiveSecondary'}
          isSquare={true}
          onClick={handleDeleteClick}
        >
          <DeleteIcon />
        </Button>
      )}
      {withCreateButtons && (
        <Button
          className={'row-button-square'}
          size={'medium'}
          data-color={'secondary'}
          isSquare={true}
          onClick={handlePlusClick}
        >
          <AddIcon />
        </Button>
      )}
    </>
  );
};
